import { User, useFeatureFlag } from 'configcat-react';
import { getTenant, getUserProfile } from '../login/loginSlice';
import { useAppSelector } from '../setup/hooks';
import { trace } from '../setup/trace';

export const FeatureToggles = {
    telematicstiles: 'telematicstiles', // comma separated, e.g. webfleet,pocketDriver,scania
    MASTERDATA_ENGINE_VEHICLE: 'masterdataEngineVehicle',
    ENABLE_ASSET_DELETE: 'enableAssetDelete', // enable delete, even though contracts are active
    POCKET_DRIVER_REGISTRATION_MAINTENANCE: 'pocketDriverRegistrationMaintenance',
    ASSET_ADMINISTRATION_MAINTENANCE: 'assetAdministrationMaintenance',
} as const;

type FeatureTogglesT = (typeof FeatureToggles)[keyof typeof FeatureToggles];

const MOCK_FT_VALUES: Record<FeatureTogglesT, string | boolean | number> = {
    telematicstiles: 'webfleet,pocketDriver,scania,schwarzmueller,koegel,idem,krone',
    masterdataEngineVehicle: true,
    enableAssetDelete: false,
    pocketDriverRegistrationMaintenance: false,
    assetAdministrationMaintenance: false,
};

export const useToggle = (featureToggleId: FeatureTogglesT, defaultValue: string | boolean | number = false) => {
    const userProfile = useAppSelector(getUserProfile);
    const tenant = useAppSelector(getTenant);
    let userObject: User;

    if (
        userProfile === null ||
        userProfile.account === undefined ||
        userProfile.sub === undefined ||
        userProfile.aud === undefined ||
        tenant === undefined
    ) {
        userObject = new User('unknown');
    } else {
        userObject = new User(userProfile.sub, undefined, userProfile.locale, {
            account: userProfile.account,
            client: userProfile.aud,
            tenant: tenant,
        });
    }
    trace(`userObject with mail ${userProfile?.email}`, userObject);
    const result = useFeatureFlag(featureToggleId, defaultValue, userObject);

    if (import.meta.env.DEV) {
        return { value: MOCK_FT_VALUES[featureToggleId], loading: false };
    } else {
        return result;
    }
};

export const useAllowAssetDeleteWithActiveContractsToggle = () => {
    const { value } = useToggle(FeatureToggles.ENABLE_ASSET_DELETE);

    return typeof value === 'boolean' ? value : false;
};

export const useIsAssetAdministrationInMaintenanceToggle = () => {
    const { value } = useToggle(FeatureToggles.ASSET_ADMINISTRATION_MAINTENANCE);

    return typeof value === 'boolean' ? value : false;
};

export const usePocketDriverRegistrationMaintenanceToggle = () => {
    const { value } = useToggle(FeatureToggles.POCKET_DRIVER_REGISTRATION_MAINTENANCE);

    return typeof value === 'boolean' ? value : false;
};
